// Americano league display/format helpers shared by the member surfaces and
// the cycle wizard. Divisions run on a whist rotation, so their sizes are
// FIXED (4, 8 or 12 players) — every player partners every other exactly once
// and opposes each twice; the schedule shape per size is a constant.
export function useAmericanoFormat() {
    // The only division sizes a whist rotation supports; the API rejects
    // anything else, so the wizard never offers other sizes.
    const VALID_SIZES = [4, 8, 12];

    // size -> {rounds, matches}: 4 → 3 matches over 3 rounds, 8 → 14 over 7,
    // 12 → 33 over 11 (n-1 rounds, n/4 courts per round).
    const SIZE_SCHEDULE = {
        4: {rounds: 3, matches: 3},
        8: {rounds: 7, matches: 14},
        12: {rounds: 11, matches: 33},
    };

    // Format identity icon (players switching partners) — one icon per entity,
    // everywhere.
    const formatIcon = 'mdi-account-switch';

    const scheduleForSize = (size) => SIZE_SCHEDULE[size] ?? null;

    // e.g. "8 players · 7 rounds · 14 matches" for the wizard's size hints.
    const sizeLabel = (size) => {
        const schedule = SIZE_SCHEDULE[size];
        if (!schedule) return `${size} players`;
        return `${size} players · ${schedule.rounds} rounds · ${schedule.matches} matches`;
    };

    // n up + n down must fit inside one division: 2n <= size (4 -> at most 2).
    const maxRolloverForSize = (size) => Math.floor((size ?? 4) / 2);

    // Standings "+/-" column: points difference always carries its sign.
    const signedDiff = (n) => (n > 0 ? `+${n}` : `${n}`);

    // Seats only ever come in fours: a new division of 4, or an existing one grown
    // 4 → 8 → 12. So `count` loose players (a waiting area, a line-up) are short of a
    // usable block by this many. 0 means they fill blocks of four exactly.
    const shortOfFour = (count) => {
        const n = Math.max(0, count ?? 0);
        return n < 4 ? 4 - n : (4 - (n % 4)) % 4;
    };

    // "1 more needs to join" / "3 more need to join" — the one phrasing, everywhere.
    const moreToJoinText = (count) => {
        const short = shortOfFour(count);
        return `${short} more ${short === 1 ? 'needs' : 'need'} to join`;
    };

    // What a cycle's waiting area means for the admin list chip: null when nobody
    // is waiting (or the API didn't send the count), `ready` when they fill seats
    // in fours ("8 waiting to be seated"), otherwise how many more must join.
    const waitingSummary = (count) => {
        if (count == null || count <= 0) return null;
        if (shortOfFour(count) === 0) return {ready: true, text: `${count} waiting to be seated`};
        return {ready: false, text: `${count} waiting · ${moreToJoinText(count)}`};
    };

    return {VALID_SIZES, formatIcon, scheduleForSize, sizeLabel, maxRolloverForSize, signedDiff,
        shortOfFour, moreToJoinText, waitingSummary};
}
